import React, { createContext, useState, useEffect, useContext } from 'react';
import { Config } from '../config';
import axios from 'axios';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [socket, setSocket] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Initialize WebSocket for global notifications
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const wsUrl = `${Config.WSBaseURL}/ws/${token}`;
    const socket = new WebSocket(wsUrl);
    
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle different notification types
        if (data.type === 'message') {
          // New message notification
          handleNewMessage(data);
        } else if (data.type === 'notification') {
          // General notification
          handleNewNotification(data);
        }
      } catch (error) {
        console.error('Error processing notification:', error);
      }
    };
    
    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    setSocket(socket);
    
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, []);
  
  // Messaging disabled — no unread count polling
  // useEffect(() => { ... polling /messages/unread/count ... }, []);
  
  // Add this effect to fetch clarifications periodically
  useEffect(() => {
    const fetchClarifications = async () => {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      if (!token) return;
      
      try {
        const response = await axios.get(`${Config.APIBaseURL}/clarifications/pending`, {
          headers: {
            'access_token': `Bearer ${token}`
          }
        });
        
        // Transform clarifications into notifications
        const clarificationNotifications = response.data.map(clarification => ({
          id: clarification.id,
          type: 'clarification',
          title: role === 'regulator' ? 'Aclaración Resuelta' : 'Aclaración Pendiente',
          subtitle: clarification.engagement_name,
          message: `Control ${clarification.control_ref}`,
          timestamp: new Date(clarification.created_at),
          read: false
        }));

        setNotifications(clarificationNotifications);
      } catch (error) {
        console.error('Error fetching clarifications:', error);
      }
    };

    // Fetch immediately and then every 10 seconds
    fetchClarifications();
    const interval = setInterval(fetchClarifications, 10000);
    
    return () => clearInterval(interval);
  }, []);
  
  const handleNewMessage = (data) => {
    // Update unread message count
    setUnreadMessages(prev => prev + 1);
    
    // Add notification
    const newNotification = {
      id: Date.now(),
      type: 'message',
      title: `Mensaje de ${data.sender_name}`,
      message: data.content.length > 30 
        ? data.content.substring(0, 30) + '...' 
        : data.content,
      timestamp: new Date(),
      read: false,
      data: data
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 5));
    
    // Show browser notification if allowed
    if (Notification.permission === 'granted') {
      new Notification(newNotification.title, {
        body: newNotification.message,
        icon: '/path/to/icon.png'
      });
    }
  };
  
  const handleNewNotification = (data) => {
    const newNotification = {
      id: Date.now(),
      type: 'notification',
      title: data.title,
      message: data.message,
      timestamp: new Date(),
      read: false,
      data: data
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 5));
    
    // Show browser notification if allowed
    if (Notification.permission === 'granted') {
      new Notification(newNotification.title, {
        body: newNotification.message,
        icon: '/path/to/icon.png'
      });
    }
  };
  
  const markMessageAsRead = (messageId) => {
    setUnreadMessages(prev => Math.max(0, prev - 1));
    
    // Mark notification as read
    setNotifications(prev => 
      prev.map(notification => 
        notification.data?.id === messageId 
          ? { ...notification, read: true } 
          : notification
      )
    );
  };
  
  const markAllMessagesAsRead = () => {
    setUnreadMessages(0);
    
    // Mark all message notifications as read
    setNotifications(prev => 
      prev.map(notification => 
        notification.type === 'message' 
          ? { ...notification, read: true } 
          : notification
      )
    );
  };
  
  const requestNotificationPermission = async () => {
    if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      await Notification.requestPermission();
    }
  };
  
  // Request browser notification permission
  useEffect(() => {
    requestNotificationPermission();
  }, []);
  
  const updateTotalUnreadCount = (count) => {
    setUnreadMessages(count);
  };
  
  // Messaging disabled — these are no-ops but kept for interface compatibility
  const syncUnreadCounts = async () => {};
  const forceUpdate = async () => {};
  
  return (
    <NotificationContext.Provider
      value={{
        unreadMessages,
        notifications,
        markMessageAsRead,
        markAllMessagesAsRead,
        requestNotificationPermission,
        updateTotalUnreadCount,
        syncUnreadCounts,
        forceUpdate
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
} 